import { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { Card } from '../ui';

interface PricePoint {
  timestamp: string;
  price: number;
  volume?: number;
}

interface PriceChartProps {
  data: PricePoint[];
  title?: string;
  showVolume?: boolean;
  height?: number;
}

export function PriceChart({ 
  data, 
  title = 'Histórico de Preços',
  showVolume = false,
  height = 300 
}: PriceChartProps) {
  const [period, setPeriod] = useState('7d');

  const formatPrice = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2
    }).format(value / 100);
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  const formatTooltipDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('pt-BR');
  };

  // Calculate price change
  const firstPrice = data[0]?.price || 0;
  const lastPrice = data[data.length - 1]?.price || 0;
  const priceChange = firstPrice > 0 ? ((lastPrice - firstPrice) / firstPrice) * 100 : 0;
  const isPositive = priceChange >= 0;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-surface-800 border border-surface-700 rounded-lg p-3 shadow-lg">
          <p className="text-surface-400 text-xs mb-1">{formatTooltipDate(label)}</p>
          <p className="text-surface-100 font-semibold">{formatPrice(payload[0].value)}</p>
          {showVolume && payload[1] && (
            <p className="text-surface-400 text-xs mt-1">
              Volume: {new Intl.NumberFormat('pt-BR').format(payload[1].value)}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-surface-100">{title}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-lg font-bold text-surface-100">{formatPrice(lastPrice)}</span>
            <span className={`text-sm ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
              {isPositive ? '+' : ''}{priceChange.toFixed(2)}%
            </span>
          </div>
        </div>
        <div className="flex gap-1 bg-surface-800 rounded-lg p-1">
          {['24h', '7d', '30d', '90d', '1y'].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                period === p ? 'bg-primary-500/20 text-primary-400' : 'text-surface-400 hover:text-surface-200'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
            <defs>
              <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={isPositive ? '#22c55e' : '#ef4444'} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={isPositive ? '#22c55e' : '#ef4444'} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <XAxis 
              dataKey="timestamp" 
              tickFormatter={formatDate}
              stroke="#64748b"
              fontSize={10}
              tickLine={false}
              axisLine={false}
            />
            <YAxis 
              tickFormatter={(v) => formatPrice(v)}
              stroke="#64748b"
              fontSize={10}
              tickLine={false}
              axisLine={false}
              width={80}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="price"
              stroke={isPositive ? '#22c55e' : '#ef4444'}
              strokeWidth={2}
              fill="url(#priceGradient)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {data.length === 0 && (
        <div className="flex items-center justify-center" style={{ height }}>
          <p className="text-surface-500">Nenhum dado disponível</p>
        </div>
      )}
    </Card>
  );
}
